import { SimpleTime, type Time } from '@ps-refarch-ux/mfe-utils';

/** UX buffer used when defaulting a “today” schedule time so it lands clearly after the current instant. */
const DEFAULT_SCHEDULE_LEAD_MS = 15 * 60 * 1000;

/** Start of local calendar day (00:00:00.000). */
export function startOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function isSameLocalCalendarDay(a: Date, b: Date): boolean {
  return startOfLocalDay(a).getTime() === startOfLocalDay(b).getTime();
}

/**
 * Combine a calendar date with wall-clock time in the user's local timezone.
 */
export function combineLocalDateAndTime(date: Date, time: Time): Date {
  const out = new Date(date);
  out.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);
  return out;
}

/**
 * Default wall-clock time for the schedule picker: morning on future days; on **today**,
 * {@link DEFAULT_SCHEDULE_LEAD_MS} after `now` when that stays the same calendar day; otherwise
 * the latest time on that day that is still strictly after `now` when representable (typically 23:59:59).
 */
export function defaultScheduleTimeForLocalDate(selectedDate: Date, now = new Date()): SimpleTime {
  if (!isSameLocalCalendarDay(selectedDate, now)) {
    return new SimpleTime(9, 0, 0);
  }
  const candidate = new Date(now.getTime() + DEFAULT_SCHEDULE_LEAD_MS);
  if (isSameLocalCalendarDay(selectedDate, candidate)) {
    return new SimpleTime(candidate.getHours(), candidate.getMinutes(), candidate.getSeconds());
  }
  const lastSecond = new SimpleTime(23, 59, 59);
  if (combineLocalDateAndTime(selectedDate, lastSecond).getTime() > now.getTime()) {
    return lastSecond;
  }
  // No whole-second wall time remains strictly after `now` on this day (extremely rare); still return end-of-day.
  return lastSecond;
}

export type ScheduleValidationResult = { ok: true } | { ok: false; timeError: string };

/**
 * Validates scheduled instant is strictly after "now" and, when date is today, time is not before now.
 */
export function validateScheduleDateTime(
  selectedDate: Date,
  time: Time | undefined
): ScheduleValidationResult {
  if (!time) {
    return { ok: false, timeError: 'Select a time.' };
  }

  const scheduled = combineLocalDateAndTime(selectedDate, time);
  const now = new Date();
  if (scheduled.getTime() <= now.getTime()) {
    if (isSameLocalCalendarDay(selectedDate, now)) {
      return {
        ok: false,
        timeError: 'Choose a time later than the current time.',
      };
    }
    return {
      ok: false,
      timeError: 'Scheduled date and time must be in the future.',
    };
  }

  return { ok: true };
}
